/**
 * Image Component for Lobby2.0
 * Handles loading images from assets CDN with proper hash management
 * Based on the old lobby's Image component implementation
 *
 * `.svg` sources render through the shared inline sprite (see ../SvgSprite): one
 * <symbol> per distinct URL instead of one isolated SVG image Document per <img>.
 * The <img> path is kept for a URL whose sprite symbol cannot be built
 * (fetch/CORS/parse failure), when a ref is forwarded (callers expect an
 * HTMLImageElement) and for the `lobby.svgSprite` localStorage opt-out.
 */

import React, { memo, useCallback, type ImgHTMLAttributes } from 'react';
import { useAssetsConfig } from 'store/app/appStore';
import { isSvgSpriteEnabled } from '../../utils/svgSpriteFlag';
import SpriteSvg, { useSpriteSymbol } from '../SvgSprite/SpriteSvg';
import styles from './Image.module.css';

interface ImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'onLoad' | 'onClick'> {
  src?: string;
  manualAffix?: string;
  loading?: 'lazy' | 'eager';
  /** Sprite-rendered SVGs have no load event: called with no argument once the symbol is mounted. */
  onLoad?: (e?: React.SyntheticEvent<HTMLImageElement>) => void;
  onClick?: React.MouseEventHandler<HTMLImageElement | SVGSVGElement>;
  ref?: React.Ref<HTMLImageElement>;
}

const SVG_URL_RE = /\.svg(\?|$)/i;

type DataAttributes = Record<`data-${string}`, unknown>;
const isDataKey = (key: string): key is `data-${string}` => key.startsWith('data-');
const pickDataAttributes = (props: Record<string, unknown>): DataAttributes => {
  const picked: DataAttributes = {};
  Object.keys(props).forEach((key) => {
    if (isDataKey(key)) picked[key] = props[key];
  });
  return picked;
};

// Stable handler references — shared by every <Image> instance, zero allocations per render.
const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
  e.currentTarget.dataset['loaded'] = '';
};

const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
  (e.currentTarget as HTMLImageElement).style.display = 'none';
};

/**
 * Marks an already-decoded image as loaded while it is still being attached.
 *
 * `onLoad` alone is not enough for one. It is a synthetic event, so it lands a task after
 * mount at the earliest, and an image that finished before the listener was attached may
 * never deliver it at all. Either way the element spends at least one frame at opacity 0
 * and then fades in — which is what reads as "loading again" when a virtualized row
 * refills on scroll-back, even though the bytes never left the cache.
 *
 * Doing it here instead puts a cached image at full opacity in its first painted frame.
 * A pending image reports `complete === false` and still fades in via `onLoad`; a broken
 * one reports `complete === true` with a zero `naturalWidth`, which the width check
 * excludes so it stays hidden for `handleError` to deal with.
 */
const markIfAlreadyDecoded = (img: HTMLImageElement | null) => {
  if (img?.complete && img.naturalWidth > 0) img.dataset['loaded'] = '';
};

const BaseImage = ({ src, manualAffix, loading = 'lazy', onLoad, onClick, className, ref, ...rest }: ImageProps) => {
  const { assetsHost, assetsHash } = useAssetsConfig();

  // Kept stable so a re-render doesn't detach and reattach the node just to re-run this.
  const setImageRef = useCallback(
    (node: HTMLImageElement | null) => {
      markIfAlreadyDecoded(node);
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.RefObject<HTMLImageElement | null>).current = node;
    },
    [ref],
  );

  // Nothing to render until assetsHost is ready; CDN environments also wait for assetsHash.
  const hostReady =
    !!assetsHost && assetsHost !== '' && !(assetsHost.includes('cdn') && (!assetsHash || assetsHash === ''));

  const rootPath = assetsHash && assetsHash.trim() !== '' ? `/${assetsHash}/assets/portal` : '/assets/portal';

  let imageSrc = '';

  if (hostReady && src) {
    if (!src.startsWith('/')) {
      imageSrc = src;
    } else if (manualAffix) {
      imageSrc = `${assetsHost}${rootPath}${src}${manualAffix}${assetsHash && assetsHash.trim() !== '' ? `?hash=${assetsHash}` : ''}`;
    } else {
      const baseSrc = src.replace(/\.[^.]+$/, '');
      imageSrc = `${assetsHost}${rootPath}${baseSrc}.webp${assetsHash && assetsHash.trim() !== '' ? `?hash=${assetsHash}` : ''}`;
    }
  }

  const spriteUrl = imageSrc && !ref && isSvgSpriteEnabled() && SVG_URL_RE.test(imageSrc) ? imageSrc : '';
  const symbol = useSpriteSymbol(spriteUrl);

  if (!hostReady) {
    return <div style={{ width: '100%', height: '100%', background: 'transparent' }} />;
  }

  const imgClassName = className ? `${styles['img']} ${className}` : styles['img'];

  if (spriteUrl && symbol === undefined) {
    // First fetch of this URL still in flight — same empty box an unloaded <img> occupies.
    return <picture />;
  }

  if (spriteUrl && symbol) {
    const { style, id, alt, ...others } = rest;
    return (
      <picture>
        <SpriteSvg
          symbol={symbol}
          className={imgClassName}
          style={style}
          id={id}
          alt={alt}
          onClick={onClick}
          onReady={onLoad}
          dataAttributes={pickDataAttributes(others)}
        />
      </picture>
    );
  }

  // If caller needs its own onLoad, wrap the stable handler; otherwise use the shared one directly.
  const loadHandler = onLoad
    ? (e: React.SyntheticEvent<HTMLImageElement>) => {
        handleLoad(e);
        onLoad(e);
      }
    : handleLoad;

  return (
    <picture>
      <img
        key={imageSrc}
        src={imageSrc}
        alt=""
        decoding="async"
        loading={loading}
        className={imgClassName}
        onLoad={loadHandler}
        onError={handleError}
        onClick={onClick}
        {...rest}
        ref={setImageRef}
      />
    </picture>
  );
};

BaseImage.displayName = 'BaseImage';

const Image = memo(BaseImage);

export default Image;
